import { Injectable, signal } from '@angular/core'
import { Store } from '@ngrx/store'
import { selectMenu } from '../../app/store/menu/menu.selector'
import { MenuItem } from '../../app/coreUi-files/containers/default-layout/dto/MenuDto'

@Injectable({
	providedIn: 'root'
})
export class MenuTitleService {
	private menuItems = signal<MenuItem[]>([])
	private routeToTitleMap = signal<Map<string, string>>(new Map())

	constructor(private store: Store) {
		this.initializeMenuMapping()
	}

	/**
	 * Initialize menu mapping from store
	 */
	private initializeMenuMapping(): void {
		this.store.select(selectMenu).subscribe(menuItems => {
			if (menuItems && menuItems.length > 0) {
				this.menuItems.set(menuItems)
				this.buildRouteToTitleMap(menuItems)
			}
		})
	}

	/**
	 * Build a map of routes to menu titles for quick lookup
	 */
	private buildRouteToTitleMap(menuItems: MenuItem[]): void {
		const routeMap = new Map<string, string>()

		menuItems.forEach(item => {
			if (item.url && item.name) {
				// Clean the URL for consistent matching
				const cleanUrl = this.cleanRoute(item.url)
				routeMap.set(cleanUrl, item.name)
				
				// Also store with leading slash for flexibility
				if (!cleanUrl.startsWith('/')) {
					routeMap.set('/' + cleanUrl, item.name)
				}
			}
		})

		this.routeToTitleMap.set(routeMap)
		console.log('MenuTitleService: Built route-to-title map with', routeMap.size, 'entries')
	}

	/**
	 * Get menu title for a given route
	 */
	getTitleForRoute(route: string): string | null {
		const cleanRoute = this.cleanRoute(route)
		const routeMap = this.routeToTitleMap()
		
		// Try exact match first
		let title = routeMap.get(cleanRoute)
		
		// Try with leading slash
		if (!title && !cleanRoute.startsWith('/')) {
			title = routeMap.get('/' + cleanRoute)
		}
		
		// Try without leading slash
		if (!title && cleanRoute.startsWith('/')) {
			title = routeMap.get(cleanRoute.substring(1))
		}
		
		// Try partial matching for nested routes
		if (!title) {
			title = this.findPartialMatch(cleanRoute, routeMap)
		}
		
		console.log('MenuTitleService: getTitleForRoute', { route, cleanRoute, title })
		return title || null
	}

	/**
	 * Find partial match for nested routes
	 */
	private findPartialMatch(route: string, routeMap: Map<string, string>): string | undefined {
		// Sort routes by length (longest first) to match most specific routes first
		const sortedRoutes = Array.from(routeMap.keys()).sort((a, b) => b.length - a.length)
		
		for (const mapRoute of sortedRoutes) {
			if (route.startsWith(mapRoute) || route.includes(mapRoute)) {
				const title = routeMap.get(mapRoute)
				if (title) {
					console.log('MenuTitleService: Found partial match', { route, mapRoute, title })
					return title
				}
			}
		}
		
		return undefined
	}

	/**
	 * Clean route for consistent matching
	 */
	private cleanRoute(route: string): string {
		let cleanPath = route
		
		// Remove hash prefix if present
		if (cleanPath.startsWith('#/')) {
			cleanPath = cleanPath.substring(2)
		}
		
		// Remove query parameters
		cleanPath = cleanPath.split('?')[0]
		
		// Remove trailing slash for consistency
		if (cleanPath.endsWith('/')) {
			cleanPath = cleanPath.slice(0, -1)
		}
		
		// Ensure leading slash for consistency
		if (!cleanPath.startsWith('/')) {
			cleanPath = '/' + cleanPath
		}
		
		return cleanPath
	}

	/**
	 * Check if a route has a corresponding menu item
	 */
	hasMenuItemForRoute(route: string): boolean {
		return this.getTitleForRoute(route) !== null
	}

	/**
	 * Get all menu items for debugging
	 */
	getAllMenuItems(): MenuItem[] {
		return this.menuItems()
	}

	/**
	 * Get the current route-to-title map for debugging
	 */
	getRouteToTitleMap(): Map<string, string> {
		return this.routeToTitleMap()
	}
}